// The two sections the alarms screen is split into, and which one the alarm button opens.
//
// Both sections read the SAME backend table. A section is a view over it, not a second store:
//   - AUTOMATION: what Cell Control produces, the cell and its machines. These behave as they did before
//     per-user subscriptions -- visible to everyone with access to alarms, and read for everyone.
//   - GENERAL: everything else. Subscriptions and per-user reading stay as they are.
//
// The names below come from sun's contract for the story (`stories/162-…/contrato-sun.md`). How a page of
// one section is requested lives in this folder's adapter, `alarmSectionsApi.js`.
const ALARM_SECTIONS = Object.freeze({ GENERAL: 'GENERAL', AUTOMATION: 'AUTOMATION' })

// What the alarm button opens when the installation has not configured it: the same view as before the
// split, so nobody notices the change until someone decides to.
const DEFAULT_ALARM_SECTION = ALARM_SECTIONS.GENERAL

// The name of the installation-wide setting in `frontEndConfig`.
const ALARM_BUTTON_SECTION_CONFIG_KEY = 'alarmButtonSection'

// The permission to see the automation section. Its own permission, not the one for alarms, so an installation
// decides who watches the cell. On deployment it is granted to every profile that has the alarms one.
const AUTOMATION_ALARMS_PERMISSION = 'AUTOMATION ERRORS'

const isAlarmSection = (value) => Object.values(ALARM_SECTIONS).includes(value)

/**
 * The section the alarm button opens, read from the installation's front-end configuration.
 *
 * Anything that is not one of the two sections -- no configuration yet, the key missing, a typo -- falls
 * back to the default rather than to an empty screen.
 *
 * @param {Object} config The `frontEndConfig` object, or a falsy value while it has not loaded
 * @returns {string} One of ALARM_SECTIONS
 */
const alarmButtonSection = (config) => {
  const value = config?.[ALARM_BUTTON_SECTION_CONFIG_KEY]
  const normalised = typeof value === 'string' ? value.trim().toUpperCase() : ''
  return isAlarmSection(normalised) ? normalised : DEFAULT_ALARM_SECTION
}

/**
 * The section the alarms open on for THIS user: the installation's choice, unless the user cannot see the
 * automation section, in which case there is only one section to open.
 *
 * @param {Object} config The `frontEndConfig` object, or a falsy value while it has not loaded
 * @param {boolean} canSeeAutomation Whether the user holds the automation permission
 * @returns {string} One of ALARM_SECTIONS
 */
const initialAlarmSection = (config, canSeeAutomation) =>
  canSeeAutomation ? alarmButtonSection(config) : ALARM_SECTIONS.GENERAL

/**
 * The two numbers the alarm button shows: the pending count of each section, separately, never added up.
 *
 * A user who cannot see the automation section gets the general count alone: a number they can never open
 * would be a badge that never clears.
 *
 * @param {{general: number, automation: number}} counts Pending alarms per section
 * @param {boolean} canSeeAutomation Whether the user holds the automation permission
 * @returns {{automation: number, general: number}} What each half of the button's badge counts; 0 hides it
 */
const alarmButtonCounts = (counts, canSeeAutomation) => ({
  automation: canSeeAutomation ? (counts?.automation ?? 0) : 0,
  general: counts?.general ?? 0
})

// Past two digits a count stops being readable at a glance. `99+` is what MUI's own badge shows.
const BADGE_MAX = 99

/**
 * A count as a badge shows it.
 *
 * @param {number} count
 * @returns {string} The count, or `99+` above 99
 */
const badgeNumber = (count) => (count > BADGE_MAX ? BADGE_MAX + '+' : String(count))

// One colour per section, shared by the alarm button and the section tabs so the two always match. Taken
// from the design the PO chose (Stitch, concept A): the theme's own error/warning are salmon and orange.
const ALARM_SECTION_COLORS = Object.freeze({
  [ALARM_SECTIONS.AUTOMATION]: '#ff5b5b',
  [ALARM_SECTIONS.GENERAL]: '#f2c94c'
})
const ALARM_SECTION_TEXT_COLOR = '#121212'

// Automation alarms are not subscribed to: everyone with the permission sees them. So they leave the
// subscription dialog -- the group, and its four types wherever they might appear.
// ⚠️ Hiding is ALL this does. Existing subscriptions to the group are left untouched on purpose: errors that
// the backend still files under this group without a type (the cluster ones) keep reaching whoever was
// already subscribed. The dialog saves one scope at a time, so a group it never shows is never written.
const AUTOMATION_SUBSCRIPTION_GROUP = 'MACHINE_AND_CELL'
const AUTOMATION_SUBSCRIPTION_TYPES = Object.freeze([
  'BLOCKED_RESOURCE',
  'PRESETTING_FAILED',
  'DEVICE_NOT_OPERATIVE_AFTER_FAILED_MOVE',
  'DEVICE_NOT_OPERATIVE_AFTER_FAILED_WORK'
])

/**
 * The alarm catalogue as the subscription dialog should offer it: without the automation group and types.
 *
 * @param {Array<Object>} catalog The groups from `alarmCatalog`, each `{ code, types: [{ code }] }`
 * @returns {Array<Object>} The same groups, minus the automation ones
 */
const subscribableCatalog = (catalog) => (Array.isArray(catalog) ? catalog : [])
  .filter((group) => group.code !== AUTOMATION_SUBSCRIPTION_GROUP)
  .map((group) => ({ ...group, types: (group.types || []).filter((type) => !AUTOMATION_SUBSCRIPTION_TYPES.includes(type.code)) }))

export {
  ALARM_SECTIONS,
  DEFAULT_ALARM_SECTION,
  ALARM_BUTTON_SECTION_CONFIG_KEY,
  AUTOMATION_ALARMS_PERMISSION,
  isAlarmSection,
  alarmButtonSection,
  initialAlarmSection,
  alarmButtonCounts,
  badgeNumber,
  ALARM_SECTION_COLORS,
  ALARM_SECTION_TEXT_COLOR,
  AUTOMATION_SUBSCRIPTION_GROUP,
  AUTOMATION_SUBSCRIPTION_TYPES,
  subscribableCatalog
}
